/**
 * ConversationBetween - Shows conversation participants with profile links
 *
 * Uses new API structure:
 * - firm_users: Array of firm user participants
 * - contacts: Array of client/contact participants
 * - groups: Array of group participants
 *
 * When there are more than VISIBLE_COUNT items the overflow is hidden behind
 * a "+X more" badge. Clicking it opens a PrimeReact OverlayPanel anchored
 * to the badge, listing the remaining participants. Groups inside the panel
 * are expandable to reveal their members.
 */

import React, { useMemo, memo, useRef, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Tooltip } from 'primereact/tooltip';
import { classNames } from 'primereact/utils';

import { useUserDetailsContext } from 'context/userDetailsContext';
import { generateFullName } from 'utils/generateFullNameUtils';
import { GroupUserTooltip } from 'components/UI/GroupUserTooltip/GroupUserTooltip';
import { titleCapitalize } from 'utils/utility_functions/titleCapitalize';
import AssignFirmUserResolution from './AssignFirmUserResolution';
import './ConversationBetween.scss';

const VISIBLE_COUNT = 3;

// ── Helpers ───────────────────────────────────────────────────────────────

function getMemberName(member) {
  return (
    member?.assignee_name || member?.recipient_name || member?.name || member?.team_member_name || generateFullName(member) || 'Unknown'
  );
}

// ── Sub-components ────────────────────────────────────────────────────────

/** Renders a single participant link (firm user or contact). */
function ParticipantLink({ item, withComma }) {
  return (
    <>
      <Link className="F-size14 text-primary-dark text-decoration-none case-details-link pointer" to={item.route}>
        {item.isStaff && <i className="fas fa-user-tie me-1" />}
        {item.label}
        {item.suffix || ''}
      </Link>
      {withComma && ', '}
    </>
  );
}

/** Renders a group label inline with a hover tooltip showing group members. */
function GroupLink({ item, withComma, tooltipClass }) {
  return (
    <>
      <Link
        className={classNames('F-size14 text-primary-dark text-decoration-none case-details-link pointer', tooltipClass)}
        to="/settings/groups"
      >
        {item.label}
        {item.archived ? ' (Group - Archived)' : ' (Group)'}
        <Tooltip target={`.${tooltipClass}`} position="top">
          {GroupUserTooltip(item.raw)}
        </Tooltip>
      </Link>
      {withComma && ', '}
    </>
  );
}

/** Single row inside the overflow panel — routes based on item kind. */
function PanelItem({ item, isExpanded, onToggleExpand }) {
  if (item.kind === 'group') {
    const members = item.members || [];
    return (
      <li className="participants-overflow-panel__item">
        <span
          className="participants-overflow-panel__group-row"
          role="button"
          tabIndex={0}
          onClick={onToggleExpand}
          onKeyDown={(e) => e.key === 'Enter' && onToggleExpand()}
          aria-expanded={isExpanded}
        >
          <Link
            to="/settings/groups"
            className="F-size14 text-primary-dark text-decoration-none case-details-link"
            onClick={(e) => e.stopPropagation()}
          >
            {item.label}
            {item.archived ? ' (Group - Archived)' : ' (Group)'}
          </Link>
          {members.length > 0 && <i className={`pi pi-chevron-${isExpanded ? 'up' : 'down'} me-2`} />}
        </span>

        {isExpanded && members.length > 0 && (
          <ul className="participants-overflow-panel__members">
            {members.map((m, mi) => (
              <li key={mi}>
                <i className="fas fa-user-tie me-1" />
                {getMemberName(m)}
              </li>
            ))}
          </ul>
        )}
      </li>
    );
  }

  if (item.kind === 'me') {
    return (
      <li className="participants-overflow-panel__item">
        <Link to={item.route} className="F-size14 text-primary-dark text-decoration-none case-details-link">
          You
        </Link>
      </li>
    );
  }

  return (
    <li className="participants-overflow-panel__item">
      <Link to={item.route} className="F-size14 text-primary-dark text-decoration-none case-details-link">
        {item.isStaff && <i className="fas fa-user-tie me-1" />}
        {item.label}
        {item.suffix || ''}
      </Link>
    </li>
  );
}

// ── Main component ────────────────────────────────────────────────────────

function ConversationBetween({ messageDetails, isFromLead, fromSidebar = false }) {
  const userContext = useUserDetailsContext();
  const currentFirmUserId = userContext?.firmDetails?.firm_user_id;
  const opRef = useRef(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = useCallback((groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  }, []);

  const getCurrentUserRoute = useCallback(() => `/settings/team-management/${currentFirmUserId}`, [currentFirmUserId]);

  const getUserRoute = useCallback((participant) => {
    if (participant.type === 'contact') return `/contacts/${participant.contact_id}`;
    return `/settings/team-management/${participant.firm_user_id}`;
  }, []);

  // ── Build flat ordered list: You → Groups → Others ──────────────────────
  const allItems = useMemo(() => {
    const firmUsers = (messageDetails?.firm_users || []).map((u) => ({
      ...u,
      type: 'firm_user',
      id: u.firm_user_id,
    }));
    const contacts = (messageDetails?.contacts || []).map((c) => ({
      ...c,
      type: 'contact',
      id: c.contact_id,
    }));
    const rawParticipants = [...firmUsers, ...contacts];

    const isCurrentUserIncluded = rawParticipants.some((p) => p.type === 'firm_user' && p.firm_user_id === currentFirmUserId);

    const items = [];

    if (isCurrentUserIncluded) {
      items.push({
        kind: 'me',
        id: 'me',
        label: 'You',
        route: getCurrentUserRoute(),
      });
    }

    (messageDetails?.groups || []).forEach((g, i) => {
      items.push({
        kind: 'group',
        id: g.group_id || g.sk || `group-${i}`,
        label: g.group_name,
        archived: g.status === 'ARCHIVED',
        members: g.members || g.firm_user_data || [],
        tooltipClass: `conv-group-tip-${(g.group_id || g.sk || i).toString().replace(/[^a-z0-9]/gi, '-')}`,
        raw: g,
      });
    });

    rawParticipants
      .filter((p) => {
        if (p.type === 'firm_user' && p.firm_user_id === currentFirmUserId) return false;
        if (fromSidebar && p.type === 'contact') return false;
        return true;
      })
      .forEach((p) => {
        let suffix = '';
        if (p.type === 'contact') {
          suffix = ` (${p.role || 'Client'})`;
        } else if (p?.user_type || p.access_level) {
          suffix = ` (${titleCapitalize(p?.user_type || p.access_level)})`;
        }
        items.push({
          kind: p.type,
          id: p.id,
          label: `${p.first_name || ''} ${p.last_name || ''}`.trim(),
          suffix,
          isStaff: p.type === 'firm_user',
          route: getUserRoute(p),
        });
      });

    return items;
  }, [
    messageDetails?.firm_users,
    messageDetails?.contacts,
    messageDetails?.groups,
    currentFirmUserId,
    fromSidebar,
    getCurrentUserRoute,
    getUserRoute,
  ]);

  const visibleItems = allItems.slice(0, VISIBLE_COUNT);
  const overflowItems = allItems.slice(VISIBLE_COUNT);

  const renderInlineItem = (item, index) => {
    const isLast = index === visibleItems.length - 1;
    const withComma = !isLast || overflowItems.length > 0;

    if (item.kind === 'group') {
      return <GroupLink key={item.id} item={item} withComma={withComma} tooltipClass={item.tooltipClass} />;
    }
    if (item.kind === 'me') {
      return (
        <React.Fragment key="me">
          <Link
            style={{ marginLeft: '0.5rem' }}
            className="F-size14 text-primary-dark text-decoration-none case-details-link pointer"
            to={item.route}
          >
            You
          </Link>
          {withComma && ', '}
        </React.Fragment>
      );
    }
    return <ParticipantLink key={item.id} item={item} withComma={withComma} />;
  };

  return (
    <div className={fromSidebar ? 'row mb-4' : 'd-flex flex-wrap justify-content-between align-items-center gap-3 mb-3'}>
      <div style={fromSidebar ? {} : { flex: '1 1 300px', minWidth: '250px', maxWidth: '100%' }}>
        <span className="F-size14">Conversation Between: </span>

        {visibleItems.map((item, index) => renderInlineItem(item, index))}

        {overflowItems.length > 0 && (
          <span
            className="participant-overflow-badge"
            role="button"
            tabIndex={0}
            onClick={(e) => {
              setExpandedGroups({});
              opRef.current?.toggle(e);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpandedGroups({});
                opRef.current?.toggle(e);
              }
            }}
            aria-label={`Show ${overflowItems.length} more participant${overflowItems.length > 1 ? 's' : ''}`}
          >
            +{overflowItems.length} more
          </span>
        )}

        <OverlayPanel ref={opRef} className="participants-overflow-panel" dismissable onHide={() => setExpandedGroups({})}>
          <ul className="participants-overflow-panel__list">
            {overflowItems.map((item) => (
              <PanelItem key={item.id} item={item} isExpanded={!!expandedGroups[item.id]} onToggleExpand={() => toggleGroup(item.id)} />
            ))}
          </ul>
        </OverlayPanel>
      </div>

      {!fromSidebar && (
        <div className="w-100">
          <AssignFirmUserResolution isFromLead={isFromLead || messageDetails?.is_lead} messageDetails={messageDetails} />
        </div>
      )}
    </div>
  );
}

ConversationBetween.propTypes = {
  messageDetails: PropTypes.shape({
    firm_users: PropTypes.arrayOf(
      PropTypes.shape({
        firm_user_id: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        email: PropTypes.string,
        profile_image: PropTypes.string,
        client_cognito_username: PropTypes.string,
        access_level: PropTypes.string,
      })
    ),
    contacts: PropTypes.arrayOf(
      PropTypes.shape({
        contact_id: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        email: PropTypes.string,
        profile_image: PropTypes.string,
        client_cognito_username: PropTypes.string,
        access_level: PropTypes.string,
        role: PropTypes.string,
      })
    ),
    groups: PropTypes.arrayOf(
      PropTypes.shape({
        group_id: PropTypes.string,
        group_name: PropTypes.string,
        status: PropTypes.string,
        members: PropTypes.array,
      })
    ),
  }),
  isFromLead: PropTypes.bool,
  refetchDetails: PropTypes.func,
  fromSidebar: PropTypes.bool,
};

ConversationBetween.defaultProps = {
  messageDetails: null,
  isFromLead: false,
  refetchDetails: () => {},
  fromSidebar: false,
};

export default memo(ConversationBetween);
